// SettingsPage.js

import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import i18n from '../i18n';
import '../css/settingsPage.css';
import logo from '../images/logo_small.png';
import { useTranslation } from 'react-i18next';
import api from "../api";

const logos = [
    { id: 1, name: "Driver", key: "driver", logo: "/images/profile_icons/driver.png" },
    { id: 2, name: "Nuclear", key: "nuclear", logo: "/images/profile_icons/nuclear.png" },
    { id: 3, name: "Firearms Expert", key: "firearmsExpert", logo: "/images/profile_icons/firearms.png" },
    { id: 4, name: "SWAT", key: "swat", logo: "/images/profile_icons/swat.png" },
    { id: 5, name: "Teleporter", key: "teleporter", logo: "/images/profile_icons/teleporter.png" },
    { id: 6, name: "Marine", key: "marine", logo: "/images/profile_icons/marine.png" },
    { id: 7, name: "Gas Soldier", key: "gasSoldier", logo: "/images/profile_icons/gas_solider.png" },
    { id: 8, name: "Seeker", key: "seeker", logo: "/images/profile_icons/seeker.png" },
    { id: 9, name: "Sniper", key: "sniper", logo: "/images/profile_icons/sniper.png" },
    { id: 10, name: "Strike Force", key: "strikeForce", logo: "/images/profile_icons/strike.png" },
    { id: 11, name: "Shotgun Master", key: "shotgunMaster", logo: "/images/profile_icons/shotgun.png" },
    { id: 12, name: "Gatling Soldier", key: "gatlingSoldier", logo: "/images/profile_icons/gatling.png" },
    { id: 13, name: "Titan", key: "titan", logo: "/images/profile_icons/titan.png" }
];

function SettingsPage() {
    const [profileData, setProfileData] = useState({
        default_language: 'en',
        favorite_character: '',
        profile_icon: null,
        timezone: '',
    });
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [email, setEmail] = useState('');
    const [subscribeToNews, setSubscribeToNews] = useState(false);
    const [userId, setUserId] = useState(null);
    const [userProfileIcon, setUserProfileIcon] = useState(null);
    const [emailError, setEmailError] = useState('');
    const [profileIconName, setProfileIconName] = useState('');
    const [timezones, setTimezones] = useState([]);
    const accessToken = localStorage.getItem('access_token');
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    useEffect(() => {
        if (!accessToken) {
            Swal.fire({
                title: t('settings.error.title'),
                text: t('settings.error.loginRequired'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            navigate('/');
            return;
        }

        try {
            const decodedToken = jwtDecode(accessToken);
            setUserId(decodedToken.id);
        } catch (error) {
            Swal.fire({
                title: t('settings.error.title'),
                text: t('settings.error.invalidToken'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            navigate('/');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (userId) {
            loadUserProfile(userId);
            loadTimezones();
        }
    }, [userId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('emailVerified') === 'true') {
            Swal.fire({
                title: t('settings.subscription.success'),
                text: t('settings.emailVerified'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            setIsEmailVerified(true);
            if (userId) {
                loadUserProfile(userId);
            }
        }
    }, [location.search, userId]);

    const loadUserProfile = (id) => {
        api
            .get(`${process.env.REACT_APP_API_URL}/api/user/${id}/profile`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then((response) => {
                const data = response.data;
                setProfileData({
                    default_language: data.default_language || 'en',
                    favorite_character: data.favorite_character || '',
                    profile_icon: null,
                    timezone: data.timezone || '',
                });
                setEmail(data.email || '');
                setIsEmailVerified(data.is_email_verified || false);
                setSubscribeToNews(data.subscribe_to_news || false);
                setUserProfileIcon(data.profile_icon || logo);

                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (!data.timezone) {
                    setProfileData(prevData => ({ ...prevData, timezone: userTimezone }));
                }
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('settings.error.fetchProfile');
                Swal.fire({
                    title: t('settings.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const loadTimezones = () => {
        api
            .get(`${process.env.REACT_APP_API_URL}/api/user/timezones`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then((response) => {
                setTimezones(response.data);
            })
            .catch((error) => {
                console.error('Error fetching timezones:', error);
            });
    };

    const handleProfileUpdate = () => {
        const formData = new FormData();
        formData.append('default_language', profileData.default_language);
        formData.append('favorite_character', profileData.favorite_character);
        formData.append('timezone', profileData.timezone);

        if (profileData.profile_icon) {
            formData.append('profile_icon', profileData.profile_icon);
        } else if (!profileData.favorite_character) {
            formData.append('profile_icon_url', 'not_specified');
        } else if (profileData.favorite_character) {
            const selectedLogo = logos.find(
                (logo) => logo.name === profileData.favorite_character
            );
            if (selectedLogo) {
                formData.append('profile_icon_url', selectedLogo.logo);
            }
        }

        api
            .post(`${process.env.REACT_APP_API_URL}/api/user/${userId}/profile`, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then(() => {
                Swal.fire({
                    title: t('settings.profile.success'),
                    text: t('settings.profile.updated'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                loadUserProfile(userId);
                i18n.changeLanguage(profileData.default_language);
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('settings.error.updateProfile');
                Swal.fire({
                    title: t('settings.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);

        if (!validateEmail(newEmail)) {
            setEmailError(t('settings.error.invalidEmailFormat'));
        } else {
            setEmailError('');
        }
    };

    const handleEmailUpdate = () => {
        if (!validateEmail(email)) {
            Swal.fire({
                title: t('settings.error.title'),
                text: t('settings.error.invalidEmail'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        api
            .post(
                `${process.env.REACT_APP_API_URL}/api/user/email`,
                { email },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            )
            .then(() => {
                Swal.fire({
                    title: t('settings.emailStatus.success'),
                    text: t('settings.emailStatus.sent'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('settings.error.updateEmail');
                Swal.fire({
                    title: t('settings.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleProfileIconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    title: t('settings.error.title'),
                    text: t('settings.error.invalidFileType'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                return;
            }

            const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSizeInBytes) {
                Swal.fire({
                    title: t('settings.error.title'),
                    text: t('settings.error.fileTooLarge'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                return;
            }

            setProfileData({ ...profileData, profile_icon: file });
            setProfileIconName(file.name);
            setEmailError('');
        }
    };

    const handleSubscriptionChange = () => {
        api
            .post(
                `${process.env.REACT_APP_API_URL}/api/user/subscription`,
                { subscribe: !subscribeToNews },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            )
            .then(() => {
                setSubscribeToNews(!subscribeToNews);
                Swal.fire({
                    title: t('settings.subscription.success'),
                    text: t('settings.subscription.updated'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('settings.error.updateSubscription');
                Swal.fire({
                    title: t('settings.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    return (
        <div className="settings-page">
            <div className="settings-page-info">
                <p id="settings-page-header-info">{t('settings.editProfile')}</p>
                <div className="profile-header">
                    <div className="profile-info"
                         onClick={(e) => {
                             e.preventDefault();
                             document.getElementById('profile-icon-upload').click();
                         }}
                    >
                        <img
                            src={userProfileIcon}
                            className="profile-icon"
                        />
                    </div>
                </div>
                <form
                    className="profile-edit-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleProfileUpdate();
                    }}
                >
                    <label>
                        {t('settings.defaultLanguage')}:
                        <select
                            value={profileData.default_language}
                            onChange={(e) => {
                                setProfileData({...profileData, default_language: e.target.value});
                                i18n.changeLanguage(e.target.value);
                            }}
                            className="language-select"
                        >
                            <option value="en">EN</option>
                            <option value="ru">RU</option>
                            <option value="ua">UA</option>
                            <option value="ko">KO</option>
                            <option value="fr">FR</option>
                        </select>
                    </label>
                    <label>
                        {t('settings.favoriteCharacter')}:
                        <select
                            value={profileData.favorite_character}
                            onChange={(e) =>
                                setProfileData({
                                    ...profileData,
                                    favorite_character: e.target.value,
                                })
                            }
                            className="character-select"
                        >
                            <option value="">{t('settings.notSpecified')}</option>
                            {logos.map((logo) => (
                                <option key={logo.id} value={logo.name}>
                                    {logo.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        {t('settings.timezone')}:
                        <select
                            value={profileData.timezone}
                            onChange={(e) =>
                                setProfileData({
                                    ...profileData,
                                    timezone: e.target.value,
                                })
                            }
                            className="timezone-select"
                        >
                            {timezones.map((tz) => (
                                <option key={tz.id} value={tz.id}>
                                    {tz.name} ({new Date().toLocaleString('en-US', {
                                    timeZone: tz.id,
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                })})
                                </option>
                            ))}
                        </select>
                    </label>
                    <input
                        type="file"
                        id="profile-icon-upload"
                        onChange={handleProfileIconChange}
                        className="profile-icon-input"
                        style={{display: 'none'}}
                    />
                    <label className="profile-icon-label">
                        {profileIconName && (
                            <span className="profile-icon-name">
                                {t('settings.profile.selectedFile')}: {profileIconName}
                            </span>
                        )}
                    </label>
                    <button type="submit" className="save-profile-button">
                        {t('settings.save')}
                    </button>
                </form>

                {isEmailVerified ? (
                    <p>{t('settings.emailVerified')}</p>
                ) : (
                    <div id="email-section">
                        <h3>{t('settings.emailSettings')}</h3>
                        <div className="email-section">
                            <label>
                                {t('settings.email')}:
                                <input
                                    type="email"
                                    value={email}
                                    onChange={handleEmailChange}
                                />
                            </label>
                            <button onClick={handleEmailUpdate}>{t('settings.updateEmail')}</button>
                            <p>{t('settings.emailNotVerified')}</p>
                        </div>
                    </div>
                )}

                <div id="subscription-section">
                    <p>{t('settings.subscriptionSettings')}</p>
                    <label>
                        <input
                            type="checkbox"
                            checked={subscribeToNews}
                            onChange={handleSubscriptionChange}
                            disabled={!isEmailVerified}
                        />
                        {t('settings.subscribeToNews')}
                    </label>
                    {!isEmailVerified && <p>{t('settings.verifyEmailToSubscribe')}</p>}
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;
